import { environment } from '@env';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

import { MANAGE_APPS_REDUCER_KEY } from '../reducers/manage-apps.reducer';
import { ManageAppsState, Service } from '../states/manage-apps.state';
import { getCurrentOrganizationId$ } from './session.selector';

const currentState = createFeatureSelector<ManageAppsState>(MANAGE_APPS_REDUCER_KEY);

export const getManageAppsOrgsMap$ = createSelector(
    currentState,
    getCurrentOrganizationId$,
    (state, organizationId) => state.organizationsMap?.[organizationId]
);

export const getManageAppsOrganization$ = createSelector(currentState, state => state.currentOrganizationId);

export const getManageAppsSubscriptionStatus$ = createSelector(currentState, state => state.subscriptionStatus);

export const getManageAppsAssetType$ = createSelector(currentState, state => state.assetType);

export const getManageAppsSearchText$ = createSelector(currentState, state => state.searchText);

export const getManageAppsSelectedBundleId$ = createSelector(currentState, state => state.selectedBundleId);

export const getManageAppsOrgIdArray$ = createSelector(currentState, state => state.orgIdArray);

export const getManageAppsAssignableSubscriptionIds$ = createSelector(currentState, state => state.assignableSubscriptionIds);

export const getManageAppsItemNames$ = createSelector(currentState, state => state.itemNames);

export const getManageAppsPageSize$ = createSelector(currentState, state => state.pageSize);

export const getManageAppsPageIndex$ = createSelector(currentState, state => state.pageIndex);

export const getManageAppsByPagination$ = createSelector(currentState, state => state.getAppsByPagination);

export const getManageAppsAssignableSubscriptionIdsByBundleId$ = createSelector(currentState, state => {
    return state.assignableSubscriptionIds[state.currentOrganizationId] || [];
});

// to get bundle subscription app ids
export const getManageAppsBundleSubscribedAppIds$ = createSelector(currentState, state => {
    return state.bundleSubscribedAppIds[state.selectedBundleId] || [];
});

export const getManageAppsOrganizationEntity$ = createSelector(currentState, state => {
    return state.organizations.entities?.[state.currentOrganizationId];
});

export const getManageAppsorgIdVsName$ = createSelector(getManageAppsOrganizationEntity$, organizationEntity => {
    return organizationEntity?.orgIdVsName;
});

export const getManageAppsServicesMap$ = createSelector(getManageAppsOrganizationEntity$, organizationEntity => {
    return organizationEntity?.services?.entities;
});

export const getManageAppsServiceIds$ = createSelector(
    getManageAppsOrganizationEntity$,
    organizationEntity => organizationEntity?.services?.ids as string[]
);

export const getManageAppsServices$ = createSelector(
    getManageAppsServicesMap$,
    getManageAppsServiceIds$,
    (servicesMap, serviceIds): Service[] => {
        return serviceIds?.reduce((items, id) => {
            items.push(servicesMap[id]);
            return items;
        }, []);
    }
);

export const getManageAppsServicesByServiceId$ = createSelector(getManageAppsServices$, services => {
    return services?.reduce((map, service) => {
        map[service.serviceId] = service;
        return map;
    }, {} as { [property: string]: Service });
});

export const manageAppsFilterParams$ = createSelector(
    getManageAppsSubscriptionStatus$,
    getManageAppsAssetType$,
    getManageAppsSearchText$,
    getManageAppsSelectedBundleId$,
    getManageAppsOrgIdArray$,
    getManageAppsAssignableSubscriptionIds$,
    getManageAppsItemNames$,
    (subscriptionStatus, assetType, searchText, selectedBundleId, orgIdArray, assignableSubscriptionIds, itemNames) => {
        return {
            subscriptionStatus,
            assetType,
            searchText,
            selectedBundleId,
            orgIdArray,
            assignableSubscriptionIds,
            itemNames,
        };
    }
);

export const getServicesByFilterWithoutPagination$ = createSelector(manageAppsFilterParams$, getManageAppsServices$, (params, services) => {
    const status = params.subscriptionStatus;
    const serviceType = params.assetType;
    const searchText = params.searchText?.length > 0 ? params.searchText : undefined;
    const bundleId = params.selectedBundleId;
    const assignableSubscriptionIds = bundleId?.length > 0 && params.assignableSubscriptionIds[bundleId];
    const orgIdsArray = params.orgIdArray?.length > 0 ? params.orgIdArray : undefined;
    const itemNames = params.itemNames?.length > 0 ? params.itemNames : undefined;
    return services
        ?.filter(service => (status === 'ALL' ? true : service.status === status))
        ?.filter(service => (serviceType === undefined ? true : service.assetType === serviceType))
        ?.filter(service => !assignableSubscriptionIds || assignableSubscriptionIds.includes(service.serviceId))
        ?.filter(service =>
            searchText === undefined ? true : (service.displayName || service.itemName).toLowerCase().includes(searchText)
        )
        ?.filter(service => !orgIdsArray || orgIdsArray.includes(service.organizationId))
        ?.filter(
            service =>
                !itemNames ||
                itemNames.includes((service.itemName || service['serviceName']).toLowerCase()) ||
                itemNames.includes(service.displayName?.toLowerCase())
        );
});

export const getManageAppsPageStatus$ = createSelector(
    getManageAppsPageSize$,
    getManageAppsPageIndex$,
    getManageAppsByPagination$,
    getServicesByFilterWithoutPagination$,
    (pageSize, pageIndex, getByPagination, services) => {
        return {
            count: services?.length,
            pageSize,
            pageIndex,
            getByPagination,
        };
    }
);

export const getServicesByFilter$ = createSelector(
    getManageAppsPageStatus$,
    getServicesByFilterWithoutPagination$,
    (pagination, services) => {
        return services?.slice(pagination.pageSize * pagination.pageIndex, pagination.pageSize * (pagination.pageIndex + 1));
    }
);

export const getServicesCountBySubscriptionStatus$ = createSelector(currentState, getManageAppsServices$, (state, services) => {
    const status = state.subscriptionStatus;
    return services?.filter(service => (status === 'ALL' ? true : service.status === status)).length;
});

export const getBridgeService$ = createSelector(currentState, getManageAppsServicesMap$, (state, servicesMap) => {
    const itemId = state.currentBridgeId;
    const id = Object.keys(servicesMap).find(key => servicesMap[key].itemId === itemId);
    return servicesMap?.[id];
});

export const getManageAppsSelectedServices$ = createSelector(currentState, state => {
    const serviceIds = state.selectedServiceIds || [];
    const services = state.organizationsMap[state?.currentOrganizationId].nonSubscribedApps;
    return services.filter(service => serviceIds.includes(service.serviceId) || serviceIds.includes(service.restApiName));
});

export const getSubscriptionApprovalData$ = createSelector(getManageAppsorgIdVsName$, getManageAppsPageStatus$, (orgIdVsName, pageData) => {
    return {
        orgIdVsName,
        pageData,
    };
});

export const getSubscriptionApprovalCount$ = createSelector(currentState, state => {
    const orgIdString = state.orgIdArray.join(',');
    const itemIdString = state.itemIdArray.join(',');
    return state.count?.[`${state.subscriptionStatus}|${orgIdString}|${itemIdString}`] || 0;
});

export const getManageAppsSelectedOrgAndAppIds$ = createSelector(currentState, state => {
    return { itemIdArray: state.itemIdArray, orgIdArray: state.orgIdArray };
});

export const getAllServicesByOrgId$ = createSelector(getManageAppsOrgsMap$, orgMap => {
    return orgMap?.nonSubscribedApps;
});

export const getAllServicesMap$ = createSelector(getAllServicesByOrgId$, allApps => {
    return allApps?.reduce((map, app) => {
        map[app.serviceId] = app;
        return map;
    }, {} as { [property: string]: Service });
});

export const getServicesByStatus$ = createSelector(getManageAppsOrgsMap$, getManageAppsSubscriptionStatus$, (orgMap, status) => {
    return orgMap?.appsByStatus?.[status];
});

export const getSubscriptionApprovalServicesByStatus$ = createSelector(
    getManageAppsOrgsMap$,
    getManageAppsSubscriptionStatus$,
    (orgMap, status) => {
        return orgMap?.subscriptionApprovalApps?.[status];
    }
);

export const getServicesWithoutPagination$ = createSelector(
    manageAppsFilterParams$,
    getAllServicesByOrgId$,
    getServicesByStatus$,
    getManageAppsBundleSubscribedAppIds$,
    (params, allServices, servicesByStatus, selectedBundleAppIds) => {
        const status = params.subscriptionStatus;
        const serviceType = params.assetType;
        const searchText = params.searchText?.length > 0 ? params.searchText : undefined;
        const bundleId = params.selectedBundleId;
        const assignableSubscriptionIds = bundleId?.length > 0 && params.assignableSubscriptionIds[bundleId];
        const orgIdsArray = params.orgIdArray?.length > 0 ? params.orgIdArray : undefined;
        const itemNames = params.itemNames?.length > 0 ? params.itemNames : undefined;
        const clonedServices = CommonUtilsService.cloneObject(params.subscriptionStatus === 'ALL' ? allServices : servicesByStatus);
        let services =
            bundleId && params.subscriptionStatus === 'APPROVED'
                ? clonedServices?.map(service => ({
                      ...service,
                      selected: selectedBundleAppIds.includes(service.serviceId),
                  }))
                : clonedServices;
        if (environment?.restrictedProcessingUIs?.includes('gst-filing')) {
            services = services?.filter(service => service.serviceType !== 'PACKAGE');
        }
        return services
            ?.filter(service => (status === 'ALL' ? true : service.status === status))
            ?.filter(service => (serviceType === undefined ? true : service.assetType === serviceType))
            ?.filter(service => !assignableSubscriptionIds || assignableSubscriptionIds.includes(service.serviceId))
            ?.filter(service =>
                searchText === undefined ? true : (service.displayName || service.itemName).toLowerCase().includes(searchText)
            )
            ?.filter(service => !orgIdsArray || orgIdsArray.includes(service.organizationId))
            ?.filter(
                service =>
                    !itemNames ||
                    itemNames.includes((service.itemName || service['serviceName']).toLowerCase()) ||
                    itemNames.includes(service.displayName?.toLowerCase())
            );
    }
);

export const getManageAppsSelectedServicesIds$ = createSelector(currentState, state => {
    return state?.selectedServiceIds || [];
});

export const getServicesByPagination$ = createSelector(
    getManageAppsPageStatus$,
    getServicesWithoutPagination$,
    getManageAppsSelectedServicesIds$,
    (_, props: { isFromPackagePage?: boolean; isReadOnly?: boolean } = {}) => props,
    (pagination, services, selectedServiceIds, props) => {
        const { isFromPackagePage = false, isReadOnly = false } = props;
        if (!services) return [];
        let filteredServices = services;
        if (isReadOnly) {
            filteredServices = filteredServices.filter(service => selectedServiceIds.includes(service.serviceId));
        }
        if (isFromPackagePage) {
            filteredServices = filteredServices.filter(app => ['ASSET', 'RECON', 'CONNECTOR'].includes(app?.assetType));
        }
        const start = pagination.pageSize * pagination.pageIndex;
        const end = start + pagination.pageSize;
        return filteredServices.slice(start, end);
    }
);

export const getServicesByPageStatus$ = createSelector(
    getManageAppsPageSize$,
    getManageAppsPageIndex$,
    getManageAppsByPagination$,
    getServicesWithoutPagination$,
    getManageAppsSelectedServicesIds$,
    (_: any, props: { isFromPackagePage?: boolean; isReadOnly?: boolean } = {}) => props,
    (pageSize, pageIndex, getByPagination, services, selectedServiceIds, props) => {
        const { isFromPackagePage = false, isReadOnly = false } = props;

        if (!services) {
            return {
                count: 0,
                pageSize,
                pageIndex,
                getByPagination,
            };
        }
        let filteredServices = services;
        if (isReadOnly) {
            filteredServices = filteredServices.filter(service => selectedServiceIds.includes(service.serviceId));
        }
        if (isFromPackagePage) {
            filteredServices = filteredServices.filter(service => ['ASSET', 'RECON', 'CONNECTOR'].includes(service.assetType));
        }
        return {
            count: filteredServices.length,
            pageSize,
            pageIndex,
            getByPagination,
        };
    }
);

export const getSubscriptionPagiantion$ = createSelector(currentState, state => state.subscriptionPagination);

export const getSubscriptionServicesByPagination$ = createSelector(
    getSubscriptionApprovalServicesByStatus$,
    getSubscriptionPagiantion$,
    (services, pagination) => {
        const start = pagination?.pageIndex * pagination?.pageSize;
        const end = (pagination?.pageIndex + 1) * pagination?.pageSize;
        return services?.filter((_record, index) => index >= start && index < end) || [];
    }
);

export const getSelectedOrgIdsArray$ = createSelector(currentState, state => {
    return state?.organizationIdsArray || [];
});

export const getSelectedServiceIdsArray$ = createSelector(currentState, state => {
    return state?.serviceIdsArray || [];
});

export const getSelectedBridgeId$ = createSelector(currentState, state => {
    return state?.selectedBridgeId || '';
});

export const getBridgeIdsVsMetadataIds$ = createSelector(currentState, state => {
    return state?.bridgeIdsVsMetadataIds;
});

export const getSubscriptionsCount$ = createSelector(currentState, state => {
    return state?.subscriptionsCount;
});

export const getSelectedBridgeAssetDataId$ = createSelector(
    getBridgeIdsVsMetadataIds$,
    getSelectedBridgeId$,
    (bridgeIdsVsAssetIds, serviceId) => {
        return bridgeIdsVsAssetIds?.[serviceId] || '';
    }
);

export const getSelectedBridgeAsset$ = createSelector(
    currentState,
    getSelectedBridgeId$,
    getAllServicesByOrgId$,
    getServicesByStatus$,
    getSelectedBridgeAssetDataId$,
    (state, id, allServices, servicesBystatus, assetId) => {
        if (state?.subscriptionStatus === 'ALL') {
            const service = CommonUtilsService.cloneObject(allServices?.find(service => service?.serviceId === id));
            if (service) {
                service['itemId'] = assetId;
            }
            return service;
        } else {
            const service = CommonUtilsService.cloneObject(servicesBystatus?.find(service => service?.serviceId === id));
            if (service) {
                service['itemId'] = assetId;
            }
            return service;
        }
    }
);

export const getIsGlobalRepository$ = createSelector(currentState, (state: ManageAppsState) => state.isGlobalRepository);

export const getAllApps$ = createSelector(getManageAppsOrgsMap$, orgMap => {
    return orgMap?.allApps;
});
